import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Flex,
  Text,
  Button,
  InputGroup,
  InputLeftAddon,
  Input,
  NumberInput,
  NumberInputField,
  Heading,
  Switch,
  Box,
} from "@chakra-ui/react";
import { FaPlusCircle, FaSave } from "react-icons/fa";
import { DeleteIcon, EditIcon } from "@chakra-ui/icons";
import { MdCancel, MdDragIndicator } from "react-icons/md";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "react-beautiful-dnd";

interface InputFieldProps {
  title: string;
  leftAddon?: string | any;
  min?: number;
  max?: number;
  placeHolder?: string;
  charLimit?: number;
  inputType?: "numeric" | "text" | "checkbox" | "delete";
  keyName: string;
  nullable?: boolean;
  columnSize?: number;
}

interface ComponentProps {
  width?: number | string;
  sectionTitle?: string;
  items: any[];
  originalItems?: any[];
  handleFieldChange: (e: string, idx: number, field: string) => void;
  addNewItem?: () => void;
  handleSave: () => void;
  loading?: boolean;
  processing?: boolean;
  inputFields: InputFieldProps[];
  defaultEditableValue?: boolean;
  onRemove?: (idx: number) => void;
  setItems?: (items: any) => void;
  disabledKey?: string;
  isReorderable?: boolean;
}

const MultiInputEntries: FC<ComponentProps> = ({
  width,
  sectionTitle,
  items,
  originalItems,
  handleFieldChange,
  addNewItem,
  handleSave,
  loading,
  processing,
  inputFields,
  defaultEditableValue = true,
  onRemove,
  setItems,
  disabledKey,
  isReorderable = false,
}) => {
  const [isEditable, setEditable] = useState<boolean>(defaultEditableValue);
  const [localItems, setLocalItems] = useState<any[]>(items);
  const [inputFieldsState, setInputFieldsState] =
    useState<InputFieldProps[]>(inputFields);

  const handleEditableUpdate = useCallback((editable: boolean) => {
    setEditable(editable);

    if (editable) {
      setInputFieldsState((prev) => [
        ...prev,
        {
          title: "Delete",
          inputType: "delete",
          keyName: "delete",
        },
      ]);
    } else {
      setInputFieldsState((prev) =>
        prev.filter((item) => item.keyName !== "delete")
      );
    }
  }, []);

  const handleCancel = useCallback(() => {
    if (setItems && originalItems) {
      setItems(originalItems);
      handleEditableUpdate(false);
    }
  }, [setItems, originalItems, handleEditableUpdate]);

  const totalColumnSize = useMemo(() => {
    return inputFieldsState.reduce((acc, curr) => {
      return acc + (curr.columnSize ? curr.columnSize : 1);
    }, 0);
  }, [inputFieldsState]);

  const hasItemsEmptyFields = useMemo(() => {
    return !!items.find((item) =>
      inputFieldsState.some((field) =>
        field.keyName === "delete"
          ? false
          : field.nullable
          ? false
          : !item[field.keyName]
      )
    );
  }, [items, inputFieldsState]);

  useEffect(() => {
    if (loading && items.length === 0) return;
    setLocalItems(JSON.parse(JSON.stringify(items)));
  }, [loading, items]);

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const newItems = Array.from(localItems);
    const [reorderedItem] = newItems.splice(result.source.index, 1);
    newItems.splice(result.destination.index, 0, reorderedItem);

    setLocalItems(newItems);
    if (setItems) {
      setItems(newItems);
    }
  };

  const handleLocalFieldChange = (
    value: string,
    idx: number,
    field: string
  ) => {
    const newItems = [...localItems];
    newItems[idx][field] = value;
    setLocalItems(newItems);
    handleFieldChange(value, idx, field);
  };

  const renderItemFields = (data: any, itemIdx: number) => (
    <Flex flexWrap="wrap" width="100%">
      {inputFieldsState.map((field, idx) => (
        <Box
          key={idx}
          width={`${((field.columnSize || 1) / totalColumnSize) * 100}%`}
          px={1}
          mb={2}
        >
          <InputGroup
            display={"flex"}
            alignItems="center"
            justifyContent={"center"}
            height={"100%"}
          >
            {!!field.leftAddon && <InputLeftAddon children={field.leftAddon} />}
            {field.inputType === "numeric" ? (
              <NumberInput
                onChange={(valueAsString) =>
                  handleLocalFieldChange(valueAsString, itemIdx, field.keyName)
                }
                isDisabled={!isEditable}
                value={data[field.keyName] ?? ""}
                defaultValue={data[field.keyName] ?? ""}
                min={field?.min}
                max={field?.max}
                width="100%"
              >
                <NumberInputField
                  placeholder={field.placeHolder}
                  disabled={
                    !isEditable ||
                    (disabledKey !== undefined ? !data[disabledKey] : false)
                  }
                />
              </NumberInput>
            ) : field.inputType === "checkbox" ? (
              <Flex
                width="100%"
                alignItems="center"
                justifyContent="center"
                height="100%"
              >
                <Switch
                  isChecked={data[field.keyName]}
                  colorScheme="purple"
                  isDisabled={
                    !isEditable ||
                    (field.keyName === disabledKey
                      ? false
                      : disabledKey !== undefined
                      ? !data[disabledKey]
                      : false)
                  }
                  onChange={(e) =>
                    handleLocalFieldChange(
                      e.target.checked.toString(),
                      itemIdx,
                      field.keyName
                    )
                  }
                />
              </Flex>
            ) : field.inputType === "text" ? (
              <Input
                onChange={(e) =>
                  handleLocalFieldChange(e.target.value, itemIdx, field.keyName)
                }
                value={data[field.keyName]}
                defaultValue={data[field.keyName]}
                placeholder={field.placeHolder}
                isDisabled={!isEditable}
                maxLength={field.charLimit}
                width="100%"
              />
            ) : field.inputType === "delete" ? (
              <>
                {isEditable && !!onRemove && (
                  <Button
                    onClick={() => {
                      onRemove(itemIdx);
                      if (localItems.length === 1) handleEditableUpdate(false);
                    }}
                    size="sm"
                    colorScheme="red"
                    mb={2}
                    alignSelf="center"
                  >
                    <DeleteIcon />
                  </Button>
                )}
              </>
            ) : (
              <></>
            )}
          </InputGroup>
        </Box>
      ))}
    </Flex>
  );

  return (
    <Flex
      flexDir="column"
      p={4}
      borderRadius="16"
      width={width || 400}
      mb={4}
      backgroundColor="white"
    >
      <Flex
        alignItems="center"
        justifyContent="space-between"
        minHeight="40px"
        mb={4}
      >
        {!!sectionTitle && (
          <Flex alignItems="center">
            <Heading fontSize="xl">{sectionTitle}</Heading>
            {isEditable && (
              <Box
                backgroundColor="#ff1919"
                borderRadius={40}
                marginLeft={6}
                zIndex={4}
              >
                <Text color="white" padding={1} paddingX={2} fontWeight={600}>
                  Edit Mode, Changes not Saved!
                </Text>
              </Box>
            )}
          </Flex>
        )}
        <Button
          backgroundColor="rgb(74, 52, 255)"
          _hover={{
            backgroundColor: "rgb(92,73,253)",
          }}
          color="white"
          onClick={() => {
            handleEditableUpdate(true);
          }}
          disabled={isEditable}
          size="sm"
          ml={4}
          alignSelf="flex-end"
        >
          <EditIcon boxSize={4} />
        </Button>
      </Flex>
      {localItems && localItems.length > 0 ? (
        <Box width="100%">
          <Flex
            flexWrap="wrap"
            justifyContent={"flex-end"}
            alignItems="flex-end"
            mb={4}
          >
            {inputFieldsState.map((data, itemIdx) => (
              <Box
                key={"title " + itemIdx}
                width={`${((data.columnSize || 1) / totalColumnSize) * 100}%`}
                px={1}
              >
                <Heading
                  fontSize="sm"
                  as="h3"
                  fontWeight="medium"
                  borderBottomColor="rgb(74, 52, 255)"
                  borderBottomWidth={1}
                  paddingBottom={1}
                  textAlign="center"
                >
                  {data.title}
                </Heading>
              </Box>
            ))}
          </Flex>

          {isReorderable ? (
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="items">
                {(provided) => (
                  <div {...provided.droppableProps} ref={provided.innerRef}>
                    {localItems.map((data, itemIdx) => (
                      <Draggable
                        key={itemIdx}
                        draggableId={`item-${itemIdx}`}
                        index={itemIdx}
                      >
                        {(provided) => (
                          <Flex
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            alignItems="center"
                            mb={2}
                            position="relative"
                          >
                            <Flex
                              {...provided.dragHandleProps}
                              pointerEvents={!isEditable ? "none" : "inherit"}
                              opacity={!isEditable ? 0 : 1}
                              pos={"absolute"}
                              left={-6}
                              mb={2}
                            >
                              <MdDragIndicator size={24} />
                            </Flex>
                            {renderItemFields(data, itemIdx)}
                          </Flex>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          ) : (
            localItems.map((data, itemIdx) => (
              <Flex key={itemIdx} alignItems="center" mb={2} pos="relative">
                {renderItemFields(data, itemIdx)}
              </Flex>
            ))
          )}
        </Box>
      ) : (
        <Text mx={16} textAlign="center"></Text>
      )}

      <Flex mt={4} gap={4} alignItems="center">
        {isEditable && setItems !== undefined ? (
          <Button
            borderRadius={8}
            leftIcon={<MdCancel />}
            colorScheme="red"
            size="sm"
            onClick={() => {
              handleEditableUpdate(false);
              handleCancel();
            }}
          >
            <Text>Discard Changes</Text>
          </Button>
        ) : null}
        {(isEditable || items.length === 0) && addNewItem !== undefined && (
          <Button
            borderRadius={8}
            leftIcon={<FaPlusCircle />}
            borderColor="rgb(74, 52, 255)"
            variant="outline"
            _hover={{
              borderColor: "rgb(92,73,253)",
            }}
            size="sm"
            color="rgb(74, 52, 255)"
            onClick={() => {
              if (!isEditable) handleEditableUpdate(true);
              addNewItem();
            }}
            isLoading={loading}
          >
            <Text>Add new item</Text>
          </Button>
        )}
        {isEditable && items.length > 0 ? (
          <Button
            borderRadius={8}
            leftIcon={<FaSave />}
            backgroundColor="rgb(74, 52, 255)"
            _hover={{
              backgroundColor: "rgb(92,73,253)",
            }}
            size="sm"
            color="white"
            onClick={() => {
              handleEditableUpdate(false);
              handleSave();
            }}
            disabled={hasItemsEmptyFields}
            isLoading={processing}
          >
            <Text>Save</Text>
          </Button>
        ) : null}
      </Flex>
    </Flex>
  );
};

export default MultiInputEntries;
